/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { buildNodes } from "audioProcessing/BuildNodes";
import { calculateTotalDuration } from "utilities/ClipUtilities";
import { arrayToWaveDataView } from "audioProcessing/ArrayToWaveDataView";
import {
  ClipWithPlaylistTimes,
  isClipWithAsset,
  PlaylistClip,
} from "types/Clip";
import AudioBufferCache from "audioProcessing/AudioBufferCache";
import { millisecondsToSeconds } from "TimeHelper";

export async function calculateBlobLength(
  playlist: ClipWithPlaylistTimes[],
  cache: AudioBufferCache,
  sourceSampleRate: number
): Promise<number> {
  const playlistDurationInMs = calculateTotalDuration(playlist);
  let duration;
  // POSITIVE_INFINITE is a sentinel value from EnhanceSpeech that indicates that
  // the clips should all be played to their completion.
  if (playlistDurationInMs === Number.POSITIVE_INFINITY) {
    duration = await longestBufferDuration(playlist, cache);
  } else {
    duration = millisecondsToSeconds(playlistDurationInMs);
  }
  return sourceSampleRate * duration;
}

export async function exportToBlob(
  sourceSampleRate: number,
  playlist: PlaylistClip[],
  cache: AudioBufferCache,
  outputFilename?: string
): Promise<File> {
  const length = await calculateBlobLength(playlist, cache, sourceSampleRate);
  const offlineAudioContext = new OfflineAudioContext(
    1,
    length,
    sourceSampleRate
  );
  const nodes = buildNodes(offlineAudioContext, playlist, cache);
  await Promise.all(nodes);
  const result = await offlineAudioContext.startRendering();
  const interleaved = result.getChannelData(0);

  const view = arrayToWaveDataView(offlineAudioContext.sampleRate, interleaved);

  const defaultedOutputFilename =
    outputFilename ?? `Export - ${new Date().toLocaleString()}.wav`;

  const blob = new File([view], defaultedOutputFilename, {
    type: "audio/wav",
  });
  return blob;
}

export async function longestBufferDuration(
  playlist: ClipWithPlaylistTimes[],
  cache: AudioBufferCache
): Promise<number> {
  const lengths = await Promise.all(
    playlist.map(async (clip) => {
      if (isClipWithAsset(clip)) {
        const buffer = (await cache.getAssetAudioBuffer(clip.assetId)) ?? null;
        return buffer?.duration ?? 0;
      } else {
        return 0;
      }
    })
  );
  return Math.max(...lengths);
}
